import * as React from 'react';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FormField } from '../../components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';
import { Product } from '../../../application/routes/products/ProductsScreen/types';
import { useMaintainProductMutation } from '../../../application/routes/products/CreateProductsScreen/useMaintainProductMutation';

interface ProductFormDialogProps {
  open: boolean;
  onClose: () => void;
  onProductCreated: () => void;
  selectedProduct?: Partial<Product> | null;
  productFamilies: { label: string; value: string }[];
}

interface FormValues {
  id: string;
  categoryID: string;
  name: string;
  description: string;
  listPrice: string;
  units: string;
  productCode: string;
  productFamily: string;
  frequency: string;
  status: string;
}

const initialValues: FormValues = {
  id: '',
  categoryID: '',
  name: '',
  description: '',
  listPrice: '0',
  units: '',
  productCode: '',
  productFamily: '',
  frequency: 'monthly',
  status: 'active',
};

export function ProductFormDialog({
  open,
  onClose,
  onProductCreated,
  selectedProduct,
  productFamilies,
}: ProductFormDialogProps) {
  const isEditMode = !!selectedProduct?.id;
  const toast = useToast();
  const maintainProductMutation = useMaintainProductMutation();

  const [form, setForm] = useState<FormValues>(() => {
    if (isEditMode && selectedProduct) {
      return {
        id: selectedProduct.id || '',
        categoryID: selectedProduct.categoryID || '',
        name: selectedProduct.name || '',
        description: selectedProduct.description || '',
        listPrice: String(selectedProduct.listPrice || 0),
        units: selectedProduct.units || '',
        productCode: selectedProduct.productCode || '',
        productFamily: selectedProduct.productFamily || '',
        frequency: selectedProduct.frequency || 'monthly',
        status: selectedProduct.status || 'active',
      };
    }
    return initialValues;
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof FormValues, string>>
  >({});

  // Reset form when dialog opens with new product
  React.useEffect(() => {
    if (open) {
      if (isEditMode && selectedProduct) {
        setForm({
          id: selectedProduct.id || '',
          categoryID: selectedProduct.categoryID || '',
          name: selectedProduct.name || '',
          description: selectedProduct.description || '',
          listPrice: String(selectedProduct.listPrice || 0),
          units: selectedProduct.units || '',
          productCode: selectedProduct.productCode || '',
          productFamily: selectedProduct.productFamily || '',
          frequency: selectedProduct.frequency || 'monthly',
          status: selectedProduct.status || 'active',
        });
      } else {
        setForm(initialValues);
      }
      setErrors({});
    }
  }, [open, selectedProduct, isEditMode]);

  const updateField = (field: keyof FormValues, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormValues, string>> = {};

    if (!form.id.trim()) {
      newErrors.id = 'Product ID is required';
    }
    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!form.productCode.trim()) {
      newErrors.productCode = 'Product code is required';
    }
    if (!form.productFamily.trim()) {
      newErrors.productFamily = 'Product family is required';
    }
    if (!form.listPrice || isNaN(Number(form.listPrice))) {
      newErrors.listPrice = 'Valid price is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    try {
      await maintainProductMutation.mutateAsync({
        id: form.id,
        categoryID: form.categoryID || uuidv4(),
        name: form.name,
        description: form.description,
        listPrice: Number(form.listPrice),
        units: form.units,
        productCode: form.productCode,
        productFamily: form.productFamily,
        frequency: form.frequency,
        status: form.status,
      });
      onProductCreated();
      onClose();
    } catch {
      toast.error('Error saving product');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit product' : 'Create product'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update the product details below.'
              : 'Fill in the details to create a new product.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <FormField label="Product ID *" error={errors.id}>
            <Input
              value={form.id}
              onChange={(e) => updateField('id', e.target.value)}
              placeholder="Product ID"
              disabled={isEditMode}
              className={errors.id ? 'border-destructive' : ''}
            />
          </FormField>

          <FormField label="Name *" error={errors.name}>
            <Input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Product name"
              className={errors.name ? 'border-destructive' : ''}
            />
          </FormField>

          <FormField label="Description">
            <textarea
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Product description (optional)"
              rows={3}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring resize-y"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="List Price *" error={errors.listPrice}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  $
                </span>
                <Input
                  type="number"
                  value={form.listPrice}
                  onChange={(e) => updateField('listPrice', e.target.value)}
                  placeholder="0"
                  className={`pl-7 ${
                    errors.listPrice ? 'border-destructive' : ''
                  }`}
                />
              </div>
            </FormField>

            <FormField label="Units">
              <Input
                value={form.units}
                onChange={(e) => updateField('units', e.target.value)}
                placeholder="e.g. sq ft"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Product Code *" error={errors.productCode}>
              <Input
                value={form.productCode}
                onChange={(e) => updateField('productCode', e.target.value)}
                placeholder="Code"
                className={errors.productCode ? 'border-destructive' : ''}
              />
            </FormField>

            <FormField label="Product Family *" error={errors.productFamily}>
              <Input
                value={form.productFamily}
                onChange={(e) => updateField('productFamily', e.target.value)}
                placeholder="Family"
                list="product-families"
                className={errors.productFamily ? 'border-destructive' : ''}
              />
              <datalist id="product-families">
                {productFamilies.map((pf) => (
                  <option key={pf.value} value={pf.value} />
                ))}
              </datalist>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Frequency">
              <Select
                value={form.frequency}
                onValueChange={(val) => updateField('frequency', val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="semi-annually">Semi-annually</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            <FormField label="Status">
              <Select
                value={form.status}
                onValueChange={(val) => updateField('status', val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={maintainProductMutation.isPending}
          >
            {maintainProductMutation.isPending ? (
              <Loader2 className="size-4 animate-spin mr-1.5" />
            ) : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
